import { computed, onScopeDispose, ref } from 'vue'
import { MobileID } from 'smsaero-mobileid-sdk'
import { api } from '@/api'
import { productProfile } from '@/config/productProfile'
import { useTerminalStore } from '@/stores/terminal'
import type { MobileIdProof } from '@/services/mobileIdWidget'

type Phase = 'phone' | 'checking' | 'widget' | 'starting' | 'pending' | 'code' | 'verifying' | 'success'

/** Shared by the regular login modal and the promotional banner in both products. */
export function usePhoneAuth(t: (key: string) => string) {
  const terminal = useTerminalStore()
  const phone = ref('')
  const code = ref('')
  const phase = ref<Phase>('phone')
  const method = ref<'mobile-id' | 'sms'>('mobile-id')
  const error = ref('')
  const codeError = ref('')
  const submitting = ref(false)
  const returningUser = ref(false)
  let provisionalToken: string | null = null
  let sdk: MobileID | null = null
  let generation = 0
  let request: AbortController | null = null
  let timer: ReturnType<typeof setTimeout> | undefined

  const waiting = computed(() => ['checking', 'starting', 'pending', 'verifying'].includes(phase.value))
  const codeSended = computed(() => phase.value === 'code')
  const authSuccess = computed(() => phase.value === 'success')
  const canContinue = computed(() => !submitting.value && (
    (phase.value === 'phone' && /^7\d{10}$/.test(phone.value.replace(/\D/g, ''))) ||
    (phase.value === 'code' && /^\d{4}$/.test(code.value))
  ))
  const canUseSms = computed(() => phase.value === 'phone' || (
    method.value !== 'sms' && ['starting', 'pending', 'code'].includes(phase.value)
  ))

  function disposeSession() {
    clearTimeout(timer)
    request?.abort()
    request = null
    sdk?.destroy()
    sdk = null
  }

  function reset() {
    if (provisionalToken && terminal.token === provisionalToken) {
      terminal.setToken(null)
      terminal.setProfile(null)
    }
    provisionalToken = null
    generation++
    disposeSession()
    phase.value = 'phone'
    method.value = 'mobile-id'
    code.value = ''
    error.value = ''
    codeError.value = ''
    submitting.value = false
    returningUser.value = false
  }

  function fail(key: string, run: number) {
    if (run !== generation) { return }
    generation++
    disposeSession()
    phase.value = 'phone'
    submitting.value = false
    code.value = ''
    codeError.value = ''
    error.value = t(`authModal.${key}`)
  }

  function failureKey(err: unknown) {
    const e = err as { code?: string; message?: string; response?: { status?: number; data?: { code?: string; error?: string } } }
    const codes = [e?.code, e?.message, e?.response?.data?.code, e?.response?.data?.error]
    if (codes.includes('AUTH_MOBILE_ID_NOT_CONFIGURED')) { return 'mobileIdNotConfigured' }
    if (codes.includes('AUTH_USER_NOT_FOUND')) { return 'userUnavailable' }
    if (e?.response?.status === 429) { return 'authRateLimit' }
    return 'mobileIdFailed'
  }

  async function complete(run: number) {
    if (run !== generation) { return }
    disposeSession()
    submitting.value = false
    phase.value = 'verifying'
    provisionalToken = terminal.token
    request = new AbortController()
    try {
      const profile = await terminal.getProfile(request.signal)
      if (run !== generation) { return }
      if (!profile) { throw new Error('Profile unavailable') }
      provisionalToken = null
      phase.value = 'success'
    } catch {
      if (run !== generation) { return }
      reset()
      error.value = t('authModal.profileFailed')
    }
  }

  async function startPhoneLogin() {
    if (!canContinue.value || phase.value !== 'phone') { return }
    await startSms()
  }

  function openWidget() {
    reset()
    phase.value = 'widget'
  }

  function widgetError(key: string) {
    if (phase.value === 'widget') { error.value = t(`authModal.${key}`) }
  }

  function verifyWidget(data: MobileIdProof) {
    if (phase.value === 'widget') { verifyProof(data, generation) }
  }

  function verifyProof(data: MobileIdProof, run: number) {
    if (run !== generation || phase.value === 'verifying' || phase.value === 'success') { return }
    if (!data.session_id || !data.verify_token) {
      fail('mobileIdFailed', run)
      return
    }
    phase.value = 'verifying'
    error.value = ''
    clearTimeout(timer)
    sdk?.destroy()
    sdk = null
    request = new AbortController()
    void terminal.sendAuthMobileId({ session_id: data.session_id, verify_token: data.verify_token }, request.signal)
      .then(() => complete(run))
      .catch((err: unknown) => fail(failureKey(err), run))
  }

  async function startMobileId() {
    if (!canContinue.value || phase.value !== 'phone') { return }
    reset()
    const run = generation
    const enteredPhone = phone.value
    method.value = 'mobile-id'
    phase.value = 'starting'
    const mid = new MobileID({ tokenUrl: `${api}/auth/mobile-id/token/` })
    sdk = mid
    mid.on('pending', () => {
      if (run === generation) { phase.value = 'pending' }
    })
    mid.on('otp_required', () => {
      if (run !== generation || phase.value === 'verifying') { return }
      code.value = ''
      codeError.value = ''
      phase.value = 'code'
    })
    mid.on('invalid_code', () => {
      if (run !== generation) { return }
      submitting.value = false
      code.value = ''
      codeError.value = t('authModal.wrongCode')
    })
    mid.on('rejected', () => fail('mobileIdRejected', run))
    mid.on('expired', () => fail('mobileIdExpired', run))
    mid.on('rate_limit', () => fail('authRateLimit', run))
    mid.on('error', (err) => fail(failureKey(err), run))
    mid.on('verified', (data) => {
      verifyProof({ session_id: mid.getSessionId() ?? '', verify_token: data.verify_token }, run)
    })
    // Bound waiting even if the connection never delivers a terminal SDK event.
    timer = setTimeout(() => fail('mobileIdExpired', run), 150_000)
    try {
      await mid.init()
      if (run !== generation) { mid.destroy(); return }
      await mid.start(enteredPhone)
    } catch (err) {
      fail(failureKey(err), run)
    } finally {
      if (run !== generation) { mid.destroy() }
    }
  }

  async function startSms() {
    if (!canUseSms.value || !/^7\d{10}$/.test(phone.value.replace(/\D/g, ''))) { return }
    reset()
    terminal.setToken(null)
    terminal.setProfile(null)
    const run = generation
    method.value = 'sms'
    phase.value = 'checking'
    request = new AbortController()
    try {
      const result = await terminal.sendAuthPhone({
        phone: phone.value,
        source: productProfile.authSource,
        shop: localStorage.getItem('shop_id'),
      }, request.signal)
      if (run !== generation) { return }
      // The existing endpoint either signs in a confirmed guest (when enabled
      // by the server) or sends an SMS. Never start a second verification flow.
      if (!result?.error && typeof result?.token === 'string' && result.token.trim()) {
        terminal.setToken(result.token)
        returningUser.value = true
        await complete(run)
      } else if (!result?.error && !result?.token && result?.success === true) {
        phase.value = 'code'
      } else {
        fail('smsFailed', run)
      }
    } catch (err) {
      const key = failureKey(err)
      fail(key === 'mobileIdFailed' ? 'smsFailed' : key, run)
    }
  }

  async function submitCode() {
    if (!canContinue.value || phase.value !== 'code') { return }
    const run = generation
    codeError.value = ''
    submitting.value = true
    try {
      if (method.value === 'mobile-id') {
        await sdk?.submitOTP(code.value)
      } else {
        request = new AbortController()
        const result = await terminal.sendAuthCode({ phone: phone.value, code: code.value }, request.signal)
        if (run !== generation) { return }
        if (result.token) {
          await complete(run)
        } else {
          code.value = ''
          codeError.value = t('authModal.wrongCode')
        }
      }
    } catch (err) {
      if (run !== generation) { return }
      if (method.value === 'mobile-id') { fail(failureKey(err), run) } else {
        code.value = ''
        codeError.value = t('authModal.wrongCode')
      }
    } finally {
      if (run === generation) { submitting.value = false }
    }
  }

  onScopeDispose(reset)
  return {
    phone, code, phase, method, error, codeError, submitting, returningUser,
    waiting, codeSended, authSuccess, canContinue, canUseSms,
    startPhoneLogin, startMobileId, startSms, submitCode, reset, openWidget, verifyWidget, widgetError,
  }
}
